// ─────────────────────────────────────────────────────────────
// ExportTradesModal
//
// A small dialog that lets the user pick which trades to export:
//   - Account filter (All / specific account)
//   - Date-range filter (All / 1W / 1M / 3M / YTD)
// Live count of matching trades is shown. Export is disabled when 0.
// ─────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { C } from "../../lib/colors";
import { tradesToCsv, downloadCsv, makeFilename, filterTradesByRange } from "../../lib/exportCsv";
import { toast } from "../../lib/toast";

export function ExportTradesModal({ isOpen, onClose, trades = [], accounts = [] }) {
  const [accountFilter, setAccountFilter] = useState("all"); // "all" | account_id
  const [dateRange,     setDateRange]     = useState("All");  // "All"|"1W"|"1M"|"3M"|"YTD"
  const [exporting,     setExporting]     = useState(false);

  // Live preview of what will be exported
  const filteredTrades = useMemo(() => {
    let result = trades;
    if (accountFilter !== "all") {
      result = result.filter(t => t.account_id === accountFilter);
    }
    result = filterTradesByRange(result, dateRange);
    return result;
  }, [trades, accountFilter, dateRange]);

  const count = filteredTrades.length;
  const canExport = count > 0 && !exporting;

  const handleExport = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      // Yield to the browser before the (potentially) long string build,
      // so the "Exporting..." button text actually paints first.
      await new Promise(r => setTimeout(r, 10));
      const csv = tradesToCsv(filteredTrades);
      const filename = makeFilename("trades");
      downloadCsv(filename, csv);
      toast.success(`Exported ${count} trade${count === 1 ? "" : "s"}`);
      onClose();
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Could not export trades");
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#0f111a",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          width: "min(440px, 92vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.6)",
        }}
        role="dialog"
        aria-labelledby="export-title"
      >
        {/* Header */}
        <div style={{
          padding: "18px 22px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <div>
            <div id="export-title" style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              Export Trades to CSV
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              Re-importable into FUTURES.OS
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.muted,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 16,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 22 }}>
          {/* Account filter */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Account
            </label>
            <select
              value={accountFilter}
              onChange={e => setAccountFilter(e.target.value)}
              style={{
                width: "100%",
                padding: 12,
                background: "#1a1d2e",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.text,
                fontSize: 14,
              }}
            >
              <option value="all">All accounts</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} {acc.account_type ? `— ${acc.account_type.toUpperCase()}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Date-range filter */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Date Range
            </label>
            <div style={{
              display: "flex",
              gap: 4,
              background: "#1a1d2e",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: 2,
            }}>
              {["1W", "1M", "3M", "YTD", "All"].map(opt => {
                const active = dateRange === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setDateRange(opt)}
                    style={{
                      flex: 1,
                      padding: "8px 0",
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      background: active ? C.blue + "22" : "transparent",
                      color: active ? C.blue : C.muted,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Count preview */}
          <div style={{
            padding: "14px 16px",
            background: count > 0 ? `${C.green}10` : "#1a1d2e",
            border: `1px solid ${count > 0 ? `${C.green}40` : C.border}`,
            borderRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span style={{ fontSize: 12, color: C.muted }}>
              {count > 0
                ? `Ready to export`
                : "No trades match these filters"}
            </span>
            <span style={{
              fontSize: 22,
              fontWeight: 800,
              color: count > 0 ? C.green : C.muted,
            }}>
              {count}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 22px",
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          gap: 10,
          justifyContent: "flex-end",
          background: "#0b0d19",
        }}>
          <button
            onClick={onClose}
            disabled={exporting}
            style={{
              padding: "9px 16px",
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.sub,
              cursor: exporting ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!canExport}
            title={count === 0 ? "No trades to export" : undefined}
            style={{
              padding: "9px 18px",
              background: canExport ? C.green : "#1a1d2e",
              border: `1px solid ${canExport ? C.green : C.border}`,
              borderRadius: 6,
              color: canExport ? "#000" : C.muted,
              cursor: canExport ? "pointer" : "not-allowed",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {exporting ? "Exporting…" : `Download CSV`}
          </button>
        </div>
      </div>
    </div>
  );
}
